import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './shadcn/button';
import { Card, CardContent } from './shadcn/card';
import { isLandingOnly } from '../utils/buildMode';
import { 
  Upload, 
  Brain, 
  BarChart3, 
  Play, 
  Users, 
  Zap,
  ArrowRight,
  Shield,
  Clock,
  CheckCircle,
  Star,
  Quote,
  FileText,
  Lock,
  Mail
} from 'lucide-react';

function Home() {
  const navigate = useNavigate();
  const [animatedNumbers, setAnimatedNumbers] = useState({
    videos: 0,
    companies: 0,
    accuracy: 0,
    support: 24
  });
  const [stepAnimationStates, setStepAnimationStates] = useState([
    { count: 0, isAnimating: false },
    { count: 0, isAnimating: false },
    { count: 0, isAnimating: false }
  ]);
  const stepRefs = useRef([null, null, null]);

  // Handle scroll-triggered animations for steps
  useEffect(() => {
    const handleScroll = () => {
      stepRefs.current.forEach((ref, index) => {
        if (ref) {
          const rect = ref.getBoundingClientRect();
          const isVisible = rect.top < window.innerHeight * 0.75 && rect.bottom > 0;
          
          // If step is visible, hasn't completed 5 animations yet, and isn't currently animating
          if (isVisible && stepAnimationStates[index].count < 5 && !stepAnimationStates[index].isAnimating) {
            // Start animation
            setStepAnimationStates(prev => {
              const newStates = [...prev];
              newStates[index] = {
                count: newStates[index].count + 1,
                isAnimating: true
              };
              return newStates;
            });
            
            // Stop animation after the bounce sequence completes (approximately 1.5 seconds for a bounce)
            setTimeout(() => {
              setStepAnimationStates(prev => {
                const newStates = [...prev];
                newStates[index] = {
                  ...newStates[index],
                  isAnimating: false
                };
                return newStates;
              });
            }, 1500);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on initial load
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [stepAnimationStates]);

  // Animate numbers when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedNumbers({
        videos: 5000,
        companies: 200,
        accuracy: 98.7,
        support: 24
      });
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  const features = [
    {
      icon: Upload,
      title: "Automated Video Upload",
      description: "Securely upload traffic videos with automatic timestamp extraction from the bottom-right corner."
    },
    {
      icon: Brain,
      title: "AI-Powered Analysis",
      description: "Advanced neural networks detect and count vehicle movements with high precision."
    },
    {
      icon: BarChart3,
      title: "Detailed TMC Reports",
      description: "Generate comprehensive Traffic Movement Count reports in TSV and Excel formats."
    },
    {
      icon: Shield,
      title: "Secure Data Handling",
      description: "Enterprise-grade encryption ensures your traffic data remains confidential."
    },
    {
      icon: Clock,
      title: "Rapid Processing",
      description: "Get your TMC reports within minutes, not hours, with our optimized pipeline."
    },
    {
      icon: FileText,
      title: "Multiple Export Formats",
      description: "Download reports in both TSV and Excel formats for easy integration with your systems."
    }
  ];

  const testimonials = [
    {
      name: "David Mitchell",
      role: "Transportation Engineer",
      company: "City of Vancouver",
      content: "NeuralVision's TMC application has revolutionized our traffic analysis process. The automatic timestamp extraction saves us hours of manual work.",
      rating: 5
    },
    {
      name: "Sarah Johnson",
      role: "Traffic Planning Manager",
      company: "BC Ministry of Transportation",
      content: "The accuracy of vehicle detection is impressive. We've seen a 40% reduction in post-processing time since implementing this solution.",
      rating: 5
    },
    {
      name: "Michael Chen",
      role: "Municipal Engineer",
      company: "City of Burnaby",
      content: "As a municipal government, data security was paramount. NeuralVision's secure portal gives us confidence in our data handling.",
      rating: 4
    }
  ];

  const benefits = [
    { icon: CheckCircle, text: "Dedicated onboarding support" },
    { icon: CheckCircle, text: "Compliant with municipal data standards" },
    { icon: CheckCircle, text: "24/7 dedicated technical support" },
    { icon: CheckCircle, text: "Regular accuracy improvements" }
  ];

  return (
    <div className="home-page">
      <style>{`
        @keyframes bounce-2 {
          0%, 100% { transform: translateY(0); }
          25% { transform: translateY(-15px); }
          50% { transform: translateY(0); }
          75% { transform: translateY(-15px); }
        }
        .animate-bounce-2 {
          animation: bounce-2 1.5s ease-in-out;
        }
      `}</style>
      {/* Hero Section with Animation */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-10 right-10 w-64 h-64 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-10 left-1/2 w-64 h-64 bg-indigo-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fade-in-up">
              Traffic Movement Count Analysis for NeuralVision
            </h1>
            <p className="text-xl mb-8 text-blue-100 animate-fade-in-up animation-delay-200">
              Upload traffic videos and get accurate vehicle movement counts with automatic timestamp extraction. 
              Generate TSV and Excel reports for your transportation planning needs.
              {isLandingOnly && ' Contact our team to discuss pricing and get started.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-400">
              {isLandingOnly ? (
                <>
                  <Button 
                    size="lg" 
                    onClick={() => navigate('/contact-us')}
                    className="bg-white text-blue-600 hover:bg-blue-50 transform hover:-translate-y-1 transition duration-300"
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Contact Us
                  </Button>
                  <Button 
                    variant="outline" 
                    size="lg" 
                    onClick={() => navigate('/pricing')}
                    className="border-white text-white hover:bg-white/10 transform hover:-translate-y-1 transition duration-300"
                  >
                    View Pricing
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    size="lg" 
                    onClick={() => navigate('/register')}
                    className="bg-white text-blue-600 hover:bg-blue-50 transform hover:-translate-y-1 transition duration-300"
                  >
                    Register Your Company
                  </Button>
                  <Button 
                    variant="outline" 
                    size="lg" 
                    onClick={() => navigate('/login')}
                    className="border-white text-white hover:bg-white/10 transform hover:-translate-y-1 transition duration-300"
                  >
                    Sign In
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section with Enhanced Design */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Traffic Movement Count Features
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Advanced technology for accurate traffic analysis and reporting
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index} 
                  className="group"
                >
                  <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full transform hover:-translate-y-2 bg-white rounded-xl">
                    <CardContent className="p-8 text-center">
                      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 rounded-xl w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:from-blue-600 group-hover:to-indigo-700 transition-all duration-300">
                        <Icon className="h-10 w-10 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold mb-4 text-gray-900">{feature.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50 relative">
        <div className="absolute inset-0 opacity-5 pointer-events-none overflow-hidden">
          <div className="absolute -top-48 -right-48 w-96 h-96 bg-white rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-48 -left-48 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl animate-pulse animation-delay-2000"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How NeuralVision TMC Works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              A streamlined process for accurate traffic movement analysis
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            {/* Step 1 */}
            <div className="flex flex-col md:flex-row gap-8 items-center mb-12 animate-fade-in-up">
              <div className="flex-1 w-full">
                <div 
                  ref={(el) => (stepRefs.current[0] = el)}
                  className={`bg-gradient-to-r from-blue-500 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center mb-6 shadow-lg transform hover:scale-110 transition-all duration-500 ${stepAnimationStates[0].isAnimating ? 'animate-bounce-2' : ''} mx-auto md:mx-0`}
                >
                  <span className="text-2xl font-bold text-white">1</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 animate-fade-in-up animation-delay-500 text-center md:text-left">
                  {isLandingOnly ? 'Contact Our Team' : 'Register Your Company'}
                </h3>
                <p className="text-gray-600 animate-fade-in-up animation-delay-700 leading-relaxed text-center md:text-left">
                  {isLandingOnly
                    ? 'Reach out to our team to discuss your traffic analysis needs and pricing. ' +
                      'We will walk you through the platform and get your organization set up quickly.'
                    : 'Create an account for your organization. Our registration process is quick and straightforward, ' +
                      'ensuring you can start analyzing traffic data immediately.'}
                </p>
              </div>
              <div className="flex-1 w-full">
                <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 animate-fade-in-up animation-delay-900">
                  <CardContent className="p-6">
                    <div className="bg-gradient-to-r from-blue-100 to-indigo-100 border-2 border-blue-200 rounded-xl w-full h-48 flex items-center justify-center transition-all duration-300 hover:scale-105">
                      {isLandingOnly ? (
                        <Mail className="h-16 w-16 text-blue-600 transform transition-all duration-300 hover:rotate-12" />
                      ) : (
                        <Lock className="h-16 w-16 text-blue-600 transform transition-all duration-300 hover:rotate-12" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            {/* Step 2 */}
            <div className="flex flex-col md:flex-row gap-8 items-center mb-12 animate-fade-in-up animation-delay-300">
              <div className="flex-1 w-full md:order-2">
                <div 
                  ref={(el) => (stepRefs.current[1] = el)}
                  className={`bg-gradient-to-r from-blue-500 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center mb-6 shadow-lg transform hover:scale-110 transition-all duration-500 ${stepAnimationStates[1].isAnimating ? 'animate-bounce-2' : ''} mx-auto md:mx-0`}
                >
                  <span className="text-2xl font-bold text-white">2</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 animate-fade-in-up animation-delay-800 text-center md:text-left">Upload Traffic Videos</h3>
                <p className="text-gray-600 animate-fade-in-up animation-delay-1000 leading-relaxed text-center md:text-left">
                  Upload your traffic footage through our secure interface. Our system automatically extracts 
                  timestamps from the bottom-right corner of your videos for accurate analysis.
                </p>
              </div>
              <div className="flex-1 w-full md:order-1">
                <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 animate-fade-in-up animation-delay-1200">
                  <CardContent className="p-6">
                    <div className="bg-gradient-to-r from-blue-100 to-indigo-100 border-2 border-blue-200 rounded-xl w-full h-48 flex items-center justify-center transition-all duration-300 hover:scale-105">
                      <Upload className="h-16 w-16 text-blue-600 transform transition-all duration-300 hover:-translate-y-2" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            {/* Step 3 */}
            <div className="flex flex-col md:flex-row gap-8 items-center animate-fade-in-up animation-delay-600">
              <div className="flex-1 w-full">
                <div 
                  ref={(el) => (stepRefs.current[2] = el)}
                  className={`bg-gradient-to-r from-blue-500 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center mb-6 shadow-lg transform hover:scale-110 transition-all duration-500 ${stepAnimationStates[2].isAnimating ? 'animate-bounce-2' : ''} mx-auto md:mx-0`}
                >
                  <span className="text-2xl font-bold text-white">3</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 animate-fade-in-up animation-delay-1100 text-center md:text-left">Receive TMC Reports</h3>
                <p className="text-gray-600 animate-fade-in-up animation-delay-1300 leading-relaxed text-center md:text-left">
                  Get comprehensive Traffic Movement Count reports in both TSV and Excel formats. 
                  Download your data and integrate it with your existing transportation planning tools.
                </p>
              </div>
              <div className="flex-1 w-full">
                <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 animate-fade-in-up animation-delay-1500">
                  <CardContent className="p-6">
                    <div className="bg-gradient-to-r from-blue-100 to-indigo-100 border-2 border-blue-200 rounded-xl w-full h-48 flex items-center justify-center transition-all duration-300 hover:scale-105">
                      <BarChart3 className="h-16 w-16 text-blue-600 transform transition-all duration-300 hover:scale-110" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Trusted by Transportation Professionals
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Municipal governments and transportation agencies rely on NeuralVision for accurate TMC data
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center mb-4">
                    <Quote className="h-6 w-6 text-blue-600 mr-2" />
                    <div className="flex">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-600 mb-6 italic">"{testimonial.content}"</p>
                  <div className="flex items-center">
                    <div className="relative">
                      <img 
                        src={`https://i.pravatar.cc/150?img=${index + 4}`} 
                        alt={testimonial.name}
                        className="w-16 h-16 rounded-xl object-cover border-2 border-gray-200"
                      />
                    </div>
                    <div className="ml-4">
                      <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                      <p className="text-gray-600">{testimonial.role}</p>
                      <p className="text-blue-600 font-medium">{testimonial.company}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section> */}

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6 animate-fade-in-up">
                  Why Choose NeuralVision TMC?
                </h2>
                <p className="text-gray-600 mb-6 animate-fade-in-up animation-delay-200">
                  Our Traffic Movement Count application is specifically designed for municipal governments 
                  and transportation agencies with a focus on accuracy, security, and ease of use.
                </p>
                <ul className="space-y-4">
                  {benefits.map((benefit, index) => {
                    const Icon = benefit.icon;
                    return (
                      <li 
                        key={index} 
                        className="flex items-center animate-fade-in-up"
                        style={{ animationDelay: `${300 + index * 100}ms` }}
                      >
                        <Icon className="h-5 w-5 text-green-500 mr-3" />
                        <span className="text-gray-700">{benefit.text}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-8 text-white animate-fade-in-up animation-delay-200 text-center">
                <h3 className="text-2xl font-bold mb-4">Schedule a Demo</h3>
                <p className="mb-6">
                  See NeuralVision TMC in action and discover how it can transform your traffic analysis workflow.
                </p>
                <Button 
                  onClick={() => navigate('/contact')}
                  className="bg-white text-blue-600 hover:bg-blue-50 w-full transition duration-300 transform hover:-translate-y-0.5"
                >
                  Book a Demo
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section with Animation */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="transform hover:scale-105 transition-transform duration-300">
              <div className="flex justify-center mb-2">
                <Play className="h-8 w-8 text-white" />
              </div>
              <div className="text-3xl md:text-4xl font-bold mb-2">
                {animatedNumbers.videos.toLocaleString()}+
              </div>
              <div className="text-blue-100">Videos Processed</div>
            </div>
            <div className="transform hover:scale-105 transition-transform duration-300">
              <div className="flex justify-center mb-2">
                <Users className="h-8 w-8 text-white" />
              </div>
              <div className="text-3xl md:text-4xl font-bold mb-2">
                {animatedNumbers.companies.toLocaleString()}+
              </div>
              <div className="text-blue-100">Registered Companies</div>
            </div>
            <div className="transform hover:scale-105 transition-transform duration-300">
              <div className="flex justify-center mb-2">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <div className="text-3xl md:text-4xl font-bold mb-2">
                {animatedNumbers.accuracy}%
              </div>
              <div className="text-blue-100">Accuracy Rate</div>
            </div>
            <div className="transform hover:scale-105 transition-transform duration-300">
              <div className="flex justify-center mb-2">
                <BarChart3 className="h-8 w-8 text-white" />
              </div>
              <div className="text-3xl md:text-4xl font-bold mb-2">
                {animatedNumbers.support}/7
              </div>
              <div className="text-blue-100">Support</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-white rounded-full mix-blend-multiply filter blur-2xl animate-float"></div>
          <div className="absolute top-1/3 right-1/4 w-48 h-48 bg-blue-300 rounded-full mix-blend-multiply filter blur-2xl animate-float animation-delay-2000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-56 h-56 bg-indigo-300 rounded-full mix-blend-multiply filter blur-2xl animate-float animation-delay-4000"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Transform Your Traffic Analysis?
            </h2>
            <p className="text-xl mb-8 text-blue-100">
              {isLandingOnly
                ? 'Contact our team to discuss pricing, volume discounts, and how NeuralVision fits your workflow.'
                : 'Join transportation professionals using NeuralVision for accurate Traffic Movement Count data.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {isLandingOnly ? (
                <Button 
                  size="lg" 
                  onClick={() => navigate('/contact-us')}
                  className="bg-white text-blue-600 hover:bg-blue-50 transform hover:-translate-y-1 transition duration-300"
                >
                  Contact Our Team
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button 
                  size="lg" 
                  onClick={() => navigate('/register')}
                  className="bg-white text-blue-600 hover:bg-blue-50 transform hover:-translate-y-1 transition duration-300"
                >
                  Register Your Company
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
              <Button 
                variant="outline" 
                size="lg" 
                onClick={() => navigate('/contact')}
                className="border-white text-white hover:bg-white/10 transform hover:-translate-y-1 transition duration-300"
              >
                Contact Sales
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;